import { Link } from "react-router-dom";
import { ArrowRight, Play, Pause, Headphones } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import Parallax from "@/components/Parallax";
import GradientOrbs from "@/components/GradientOrbs";
import { DotWaterhole, DotStarburst } from "@/components/AboriginalPatterns";
import { DotCluster, WavyDotField } from "@/components/DecorativePatterns";
import { useAudio } from "@/contexts/AudioContext";

const HeroSection = () => {
  const { isPlaying, isVisible, togglePlay } = useAudio();

  return (
  <section className="relative min-h-[92vh] flex items-center overflow-hidden">
    <GradientOrbs />
    <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.03] via-transparent to-transparent" />
    <Parallax className="absolute -top-20 -right-20" offset={-60} offsetX={20}>
      <DotWaterhole className="text-primary opacity-25" size={600} />
    </Parallax>
    <Parallax className="absolute bottom-0 left-0" offset={40} offsetX={-15}>
      <DotCluster className="text-ochre opacity-20" size={350} />
    </Parallax>
    <Parallax className="absolute bottom-20 right-10" offset={-30}>
      <DotStarburst className="text-violet opacity-10" size={180} />
    </Parallax>
    <WavyDotField className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-clay opacity-[0.08] w-full" rows={8} cols={30} />

    <div className="relative max-w-5xl mx-auto px-6 py-28 md:py-36">
      <FadeIn>
        <p className="text-sm md:text-base font-semibold uppercase tracking-[0.18em] text-primary/70 mb-10 leading-relaxed">
          Engineering Organizational Endurance.<br />
          In High-Stakes Mergers and Acquisitions.
        </p>
      </FadeIn>
      <FadeIn delay={0.1} initiallyVisible>
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-foreground mb-8 tracking-tight">
          Execution Risk in M&A<br />
          <span className="text-gradient">Doesn't Start After Close.</span>
        </h1>
      </FadeIn>
      <FadeIn delay={0.2}>
        <p className="text-lg md:text-2xl text-muted-foreground font-light leading-relaxed mb-12 max-w-3xl">
          The target's leadership habits are already set before you begin diligence.
          Most execution risk sits one layer below the executive team — in how managers
          make decisions, give feedback, and hold people accountable. The question is
          whether that layer holds under integration pressure.
        </p>
      </FadeIn>
      <FadeIn delay={0.4}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            onClick={togglePlay}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 hover:border-primary/30 smooth group hover:-translate-y-px"
          >
            {isVisible && isPlaying ? <Pause size={14} /> : <Headphones size={14} />}
            {isVisible && isPlaying ? "Pause Briefing" : "Listen While You Work"}
          </button>
          <Link
            to="/where-risk-forms"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 hover:border-primary/30 smooth group hover:-translate-y-px"
          >
            Explore Where Risk Forms
            <ArrowRight size={15} className="group-hover:translate-x-1 smooth" />
          </Link>
        </div>
      </FadeIn>
    </div>
  </section>
  );
};

export default HeroSection;
